import type { AnyRouter } from "@tanstack/router-core";
import posthog, { type CaptureResult } from "posthog-js";
import type { ChatDomain } from "@/domains/chat/types";

export type WrappedPeriodKind = "latest" | "historical";
export type MutationEntity =
  | "transaction"
  | "account"
  | "category"
  | "tag"
  | "budget"
  | "installment"
  | "saving"
  | "saving_pocket"
  | "profile";
export type MutationOperation = "create" | "update" | "change_password" | "delete";
export type MutationResult = "success" | "failure";
export type MutationFailureKind =
  | "validation"
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "conflict"
  | "server"
  | "network"
  | "unknown";
export type ProfileChangedField =
  | "first_name"
  | "last_name"
  | "phone"
  | "theme"
  | "language"
  | "password";

export type EntityMutationProperties = {
  platform: "web";
  entity: MutationEntity;
  operation: MutationOperation;
  result: MutationResult;
  failure_kind?: MutationFailureKind;
  status_code?: number;
  changed_fields?: ProfileChangedField[];
};

type AnalyticsEventProperties = {
  "$pageview": { pathname: string };
  entity_mutation_completed: EntityMutationProperties;
  chat_question_completed: { domain: ChatDomain };
  wrapped_viewed: { period_kind: WrappedPeriodKind };
};

type AnalyticsEventName = keyof AnalyticsEventProperties;

const PROFILE_CHANGED_FIELD_ORDER: ProfileChangedField[] = [
  "first_name",
  "last_name",
  "phone",
  "theme",
  "language",
  "password",
];

const PROFILE_CHANGED_FIELD_ALIASES: Record<string, ProfileChangedField> = {
  first_name: "first_name",
  firstname: "first_name",
  firstName: "first_name",
  last_name: "last_name",
  lastname: "last_name",
  lastName: "last_name",
  phone: "phone",
  theme: "theme",
  language: "language",
  password: "password",
};

const NETWORK_ERROR_CODES = new Set([
  "ERR_NETWORK",
  "ECONNABORTED",
  "ETIMEDOUT",
  "ENETUNREACH",
  "ECONNRESET",
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isHttpStatusCode = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 100 && value <= 599;

export const getMutationStatusCode = (error: unknown): number | undefined => {
  if (!isRecord(error)) return undefined;

  const response = error.response;
  const responseStatus = isRecord(response) ? response.status : undefined;
  if (isHttpStatusCode(responseStatus)) return responseStatus;

  return isHttpStatusCode(error.status) ? error.status : undefined;
};

export const classifyMutationFailure = (error: unknown): MutationFailureKind => {
  const statusCode = getMutationStatusCode(error);

  if (statusCode === 401) return "unauthorized";
  if (statusCode === 403) return "forbidden";
  if (statusCode === 404) return "not_found";
  if (statusCode === 409) return "conflict";
  if (statusCode !== undefined) {
    if (statusCode >= 400 && statusCode < 500) return "validation";
    if (statusCode >= 500) return "server";
  }

  if (isRecord(error)) {
    if (typeof error.code === "string" && NETWORK_ERROR_CODES.has(error.code)) {
      return "network";
    }

    if (
      error.name === "NetworkError" ||
      error.message === "Network Error" ||
      error.message === "Failed to fetch"
    ) {
      return "network";
    }
  }

  return "unknown";
};

export const normalizeProfileChangedFields = (
  fields: readonly string[],
): ProfileChangedField[] => {
  const normalizedFields = new Set<ProfileChangedField>();

  for (const field of fields) {
    const normalizedField = PROFILE_CHANGED_FIELD_ALIASES[field];
    if (normalizedField) normalizedFields.add(normalizedField);
  }

  return PROFILE_CHANGED_FIELD_ORDER.filter((field) => normalizedFields.has(field));
};

export type EntityMutationCapture = {
  entity: MutationEntity;
  operation: MutationOperation;
  result: MutationResult;
  error?: unknown;
  statusCode?: number;
  changedFields?: readonly string[];
};

export const captureEntityMutation = ({
  entity,
  operation,
  result,
  error,
  statusCode,
  changedFields,
}: EntityMutationCapture) => {
  const properties: EntityMutationProperties = {
    platform: "web",
    entity,
    operation,
    result,
  };
  const resolvedStatusCode = isHttpStatusCode(statusCode)
    ? statusCode
    : getMutationStatusCode(error);

  if (result === "failure") {
    properties.failure_kind = classifyMutationFailure(
      error ?? (resolvedStatusCode === undefined ? undefined : { status: resolvedStatusCode }),
    );
  }

  if (resolvedStatusCode !== undefined) properties.status_code = resolvedStatusCode;

  if (entity === "profile" && changedFields !== undefined) {
    properties.changed_fields = normalizeProfileChangedFields(changedFields);
  }

  captureEvent("entity_mutation_completed", properties);
};

const SENSITIVE_PROPERTY_NAMES = [
  "email",
  "phone",
  "firstname",
  "lastname",
  "first_name",
  "last_name",
  "name",
  "description",
  "amount",
  "balance",
  "currency",
  "prompt",
  "message",
  "answer",
  "summary",
  "token",
  "access_token",
  "refresh_token",
  "payload",
  "request",
  "response",
  "error",
  "id",
  "user_id",
  "account_id",
  "transaction_id",
  "category_id",
  "tag_id",
  "installment_id",
  "saving_plan_id",
  "saving_pocket_id",
] as const;

const SENSITIVE_PROPERTY_SET = new Set<string>(SENSITIVE_PROPERTY_NAMES);
const URL_PROPERTY_NAMES = new Set([
  "$current_url",
  "$pathname",
  "$initial_current_url",
  "$session_entry_url",
]);
const REFERRER_PROPERTY_NAMES = new Set([
  "referrer",
  "referring_domain",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
]);

let initializationAttempted = false;
let isInitialized = false;
let analyticsSessionActive = false;
let identifiedUserId: string | null = null;

export const normalizePathname = (pathname: string) => {
  const pathWithoutUrlData = pathname.split(/[?#]/u, 1)[0] ?? "/";
  const normalizedPath = pathWithoutUrlData.replace(/\/+/gu, "/").replace(/\/$/u, "");
  return normalizedPath || "/";
};

const currentPathname = () =>
  typeof window === "undefined" ? "/" : normalizePathname(window.location.pathname);

const normalizePostHogPropertyName = (key: string) =>
  key.toLowerCase().replace(/^\$(?:initial_|session_entry_)/u, "$");

const sanitizePropertyBag = (
  properties: Record<string, unknown>,
): Record<string, unknown> => {
  const sanitized = { ...properties };
  const pathname = currentPathname();

  for (const key of Object.keys(sanitized)) {
    const normalizedKey = normalizePostHogPropertyName(key);
    const propertyName = normalizedKey.replace(/^\$/u, "");

    if (SENSITIVE_PROPERTY_SET.has(propertyName) || REFERRER_PROPERTY_NAMES.has(propertyName)) {
      delete sanitized[key];
      continue;
    }

    if (URL_PROPERTY_NAMES.has(normalizedKey)) {
      sanitized[key] = pathname;
    }
  }

  return sanitized;
};

export const sanitizePostHogCapture = (capture: CaptureResult | null): CaptureResult | null => {
  if (!capture) return null;

  const sanitized: CaptureResult = {
    ...capture,
    properties: sanitizePropertyBag(capture.properties),
  };

  if (capture.$set) sanitized.$set = sanitizePropertyBag(capture.$set);
  if (capture.$set_once) sanitized.$set_once = sanitizePropertyBag(capture.$set_once);

  return sanitized;
};

const getPostHogConfig = () => {
  const key = process.env.VITE_POSTHOG_PROJECT_KEY?.trim();
  const host = process.env.VITE_POSTHOG_HOST?.trim().replace(/\/+$/u, "");

  if (
    process.env.VITE_POSTHOG_ENABLED !== "true" ||
    !key ||
    !host ||
    typeof window === "undefined"
  ) {
    return null;
  }

  return { key, host };
};

export const initializeAnalytics = (router: AnyRouter) => {
  if (initializationAttempted) return;
  initializationAttempted = true;

  const config = getPostHogConfig();
  if (!config) return;

  try {
    posthog.init(config.key, {
      api_host: config.host,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_performance: false,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      capture_exceptions: false,
      disable_session_recording: true,
      disable_external_dependency_loading: true,
      advanced_disable_flags: true,
      disable_capture_url_hashes: true,
      save_referrer: false,
      save_campaign_params: false,
      persistence: "memory",
      person_profiles: "identified_only",
      respect_dnt: true,
      property_denylist: [...SENSITIVE_PROPERTY_NAMES],
      before_send: sanitizePostHogCapture,
    });
    isInitialized = true;
    analyticsSessionActive = true;
  } catch {
    return;
  }

  router.subscribe("onResolved", ({ toLocation }) => {
    captureEvent("$pageview", { pathname: normalizePathname(toLocation.pathname) });
  });
};

export const captureEvent = <TEvent extends AnalyticsEventName>(
  eventName: TEvent,
  properties?: AnalyticsEventProperties[TEvent],
) => {
  if (!isInitialized) return;

  if (properties === undefined) {
    posthog.capture(eventName);
    return;
  }

  posthog.capture(eventName, properties);
};

export const identifyAnalyticsUser = (userId: string) => {
  const normalizedUserId = String(userId).trim();
  if (!isInitialized || !normalizedUserId || identifiedUserId === normalizedUserId) return;

  if (identifiedUserId !== null) resetAnalyticsUser();
  posthog.identify(normalizedUserId);
  identifiedUserId = normalizedUserId;
  analyticsSessionActive = true;
};

export const resetAnalyticsUser = () => {
  identifiedUserId = null;
  if (!isInitialized || !analyticsSessionActive) return;

  posthog.reset();
  analyticsSessionActive = false;
};
